// RateAddEditModal.js
import React, { useState, useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import LockIcon from '@mui/icons-material/Lock';
import { DateInput, Input } from "../../../../../common/Input";
import { useEnumsApi } from "../../../../hooks/useEnumsApi";
import { MaintenanceSalaryCombinationCodePickerSelect } from "../../../../../common/selectComponents/MaintenanceSalaryCombinationCodePicker";
import { ConfirmationModal } from "../../../../../common/Modal";

// hide for now but dont remove, since where utilizing the Operation Benefit
const showAllowance = false;

const initialFormState = {
  effectiveDate: null,
  maintenanceSalaryCombinationCodeId: null,
  dailyRate: 0,
  allowance: 0,
  details: []
};

/**
 * RateAddEditModal - Professional "Locked Criteria" Version
 * This modal allows editing of rate values while keeping the matching criteria 
 * fixed based on the current filter state.
 */
const RateAddEditModal = ({ mode = "Add", isOpen, onClose, selectedItem, onSave }) => {
  const { maintenanceTypes, fetchMaintenanceData } = useEnumsApi();
  const [valueLists, setValueLists] = useState({});
  const [errors, setErrors] = useState({});

  const [form, setForm] = useState(initialFormState);

  // Confirmation Modal State
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedCodeLabel, setSelectedCodeLabel] = useState("");

  useEffect(() => {
    if (selectedItem) {
      setForm({
        ...selectedItem,
        effectiveDate: selectedItem.effectiveDate ?? null,
        maintenanceSalaryCombinationCodeId: selectedItem.maintenanceSalaryCombinationCodeId ?? null,
        dailyRate: selectedItem.dailyRate ?? 0,
        allowance: selectedItem.allowance ?? 0,
        details: selectedItem.details ?? []
      });
      setErrors({});
      setShowConfirmModal(false);

      if (selectedItem.maintenanceSalaryCombinationCodeCode) {
        setSelectedCodeLabel(selectedItem.maintenanceSalaryCombinationCodeCode);
      } else {
        setSelectedCodeLabel("");
      }

      // Load the readable text for all maintenance values
      if (selectedItem.details?.length > 0) {
        const typeIds = selectedItem.details.map(d => d.maintenanceTypeId);
        loadValuesForTypes(typeIds);
      }
    }
  }, [selectedItem, isOpen]);

  const loadValuesForTypes = async (typeIds) => {
    const missingIds = typeIds.filter(id => !valueLists[id]);
    if (missingIds.length === 0) return;

    const data = await fetchMaintenanceData(missingIds);
    if (data) {
      setValueLists(prev => ({ ...prev, ...data }));
    }
  };

  // Find the text label for a specific maintenance type and value
  const getLookupText = (typeId, valueId) => {
    // 1. Find the Category Name (e.g., "Employee Type")
    const type = maintenanceTypes.find(t =>
      t.value?.toString() === typeId?.toString()
    );

    // 2. Get the list for this type
    const list = valueLists[typeId?.toString()] || [];

    // 3. Find the specific value object
    // Checks both 'value' (your sample) and 'id' (legacy)
    const item = list.find(v => {
      const compareId = v.value !== undefined ? v.value : v.id;
      return compareId?.toString() === valueId?.toString();
    });

    return {
      typeName: type ? type.name : "Unknown",
      valueName: item
        ? (item.label || "N/A")
        : "Loading..."
    };
  };

  const validateForm = () => {
    const newErrors = {};
    const rateValue = parseFloat(form.dailyRate);
    if (form.effectiveDate === "" || form.effectiveDate === null)
      newErrors.effectiveDate = "Effective Date is required";
    if (form.maintenanceSalaryCombinationCodeId === "" || form.maintenanceSalaryCombinationCodeId === null)
      newErrors.maintenanceSalaryCombinationCodeId = "Salary Code is required";
    if (form.dailyRate === "" || form.dailyRate === null || form.dailyRate === undefined) {
      newErrors.dailyRate = "Rate is required";
    } else if (isNaN(rateValue) || rateValue <= 0) {
      newErrors.dailyRate = "Rate must be greater than 0";
    }
    // if (form.allowance === "" || form.allowance === null)
    //   newErrors.allowance = "Allowance is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInternalSave = () => {
    if (validateForm()) {
      setShowConfirmModal(true);
    }
  };

  const handleConfirmSave = () => {
    setShowConfirmModal(false);
    onSave(form);
  };

  // Format confirmation dialog content
  const renderConfirmationMessage = () => {
    const formattedDate = form.effectiveDate
      ? new Date(form.effectiveDate).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
      : "N/A";

    const codeDisplay = selectedCodeLabel
      ? selectedCodeLabel
      : (form.maintenanceSalaryCombinationCodeId ? `ID: ${form.maintenanceSalaryCombinationCodeId}` : "N/A");

    return (
      <div className="space-y-3 text-left text-xs">
        <p className="text-gray-600 font-medium text-sm">
          Are you sure you want to {mode === "Add" ? "create" : "update"} this rate rule?
        </p>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-2">
          <div className="grid grid-cols-2 gap-2 border-b border-gray-200 pb-2">
            <div>
              <span className="text-gray-400 block font-semibold uppercase text-[10px]">Effective Date</span>
              <span className="font-bold text-gray-800 text-sm">{formattedDate}</span>
            </div>
            <div>
              <span className="text-gray-400 block font-semibold uppercase text-[10px]">Daily Rate</span>
              <span className="font-bold text-green-600 text-sm">{Number(form.dailyRate).toFixed(2)}</span>
            </div>
          </div>

          <div className="border-b border-gray-200 pb-2">
            <span className="text-gray-400 block font-semibold uppercase text-[10px]">Salary Combination Code</span>
            <span className="font-semibold text-gray-700">{codeDisplay}</span>
          </div>

          {showAllowance && (
            <div className="border-b border-gray-200 pb-2">
              <span className="text-gray-400 block font-semibold uppercase text-[10px]">Daily Allowance</span>
              <span className="font-semibold text-gray-700">{Number(form.allowance).toFixed(2)}</span>
            </div>
          )}

          <div>
            <span className="text-gray-400 block font-semibold uppercase text-[10px] mb-1">Applied Criteria</span>
            <div className="flex flex-wrap gap-1">
              {form.details.length > 0 ? (
                form.details.map((detail, idx) => {
                  const lookups = getLookupText(detail.maintenanceTypeId, detail.maintenanceValueId);
                  return (
                    <span key={idx} className="bg-blue-100 text-blue-800 text-[10px] px-2 py-0.5 rounded font-medium">
                      {lookups.typeName}: <strong>{lookups.valueName}</strong>
                    </span>
                  );
                })
              ) : (
                <span className="text-gray-400 italic">No specific criteria assigned</span>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-4">
        <div className="bg-white w-full max-w-2xl rounded-lg shadow-2xl flex flex-col max-h-[90vh]">

          {/* Header */}
          <div className="px-6 py-4 border-b flex justify-between items-center rounded-t-lg bg-gray-100/50">
            <div>
              <h2 className="text-xl font-bold">{mode === "Add" ? "Create" : "Update"} Rate Rule</h2>
            </div>
            <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-200 transition-colors border-none bg-transparent cursor-pointer">
              <CloseIcon className="text-gray-500" />
            </button>
          </div>

          <div className="p-8 overflow-y-auto space-y-8">

            {/* Section 1: Fixed Matching Criteria (Locked Zone) */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-blue-600">
                <LockIcon fontSize="small" />
                <h3 className="font-bold text-sm uppercase tracking-wider">Applied Matching Criteria</h3>
              </div>

              <div className="bg-blue-50/50 border border-blue-100 rounded-lg p-5 grid grid-cols-2 gap-y-4 gap-x-6">
                {form.details.length > 0 ? (
                  form.details.map((detail, index) => {
                    const lookups = getLookupText(detail.maintenanceTypeId, detail.maintenanceValueId);
                    return (
                      <div key={index} className="flex flex-col border-l-2 border-blue-200 pl-3">
                        <span className="text-[10px] font-bold text-blue-500 uppercase">{lookups.typeName}</span>
                        <span className="text-sm font-semibold text-gray-700">{lookups.valueName}</span>
                      </div>
                    );
                  })
                ) : (
                  <div className="col-span-2 text-sm text-gray-400 italic">No criteria assigned.</div>
                )}
              </div>
            </div>

            {/* Section 1.1 Date being created and effective date */}
            {selectedItem != null && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-gray-700">
                  <h3 className="font-bold text-sm uppercase tracking-wider">Rate Config Date/s</h3>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  {selectedItem.createdAt != null && (
                    <DateInput
                      label="Transaction Date:"
                      value={selectedItem.createdAt ?? null}
                      isDisabled={true}
                    />
                  )}

                  <DateInput
                    label="Effective Date:"
                    value={form.effectiveDate}
                    onChange={e => setForm({ ...form, effectiveDate: e.target.value })}
                    isRequired={true}
                    isViewOnly={mode === "Edit"}
                    error={!!errors.effectiveDate}
                    errorMessage={errors.effectiveDate}
                  />
                </div>
              </div>
            )}

            {/* Section 2: Rate Configuration (Editable Zone) */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-gray-700">
                <h3 className="font-bold text-sm uppercase tracking-wider">Rate Details</h3>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <MaintenanceSalaryCombinationCodePickerSelect
                  label="Salary Combination Code"
                  value={form.maintenanceSalaryCombinationCodeId}
                  onSelect={(val) => {
                    setForm({ ...form, maintenanceSalaryCombinationCodeId: val.id });
                    setSelectedCodeLabel(val.code || val.label || "");
                  }}
                  placeholder="--Select salary combination code--"
                  allowAll={false}
                  error={!!errors.maintenanceSalaryCombinationCodeId}
                  errorMessage={errors.maintenanceSalaryCombinationCodeId}
                  disabled={mode === "Edit"}
                />

                <Input
                  label="DailyRate"
                  type="number"
                  placeholder="Enter daily rate"
                  value={form.dailyRate}
                  onChange={(e) => setForm({ ...form, dailyRate: e.target.value })}
                  error={!!errors.dailyRate}
                  errorMessage={errors.dailyRate}
                />

                {showAllowance && (
                  <Input
                    label="Daily Allowance"
                    placeholder="0.00"
                    type="number"
                    value={form.allowance}
                    onChange={(e) => setForm({ ...form, allowance: e.target.value })}
                    error={!!errors.allowance}
                    errorMessage={errors.allowance}
                    className="bg-white"
                  />
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t flex justify-end gap-3 rounded-b-lg bg-gray-100/50">
            <button
              onClick={onClose}
              className="px-4 py-2 border rounded bg-white cursor-pointer hover:bg-gray-100"
            >
              CANCEL
            </button>
            <button
              onClick={handleInternalSave}
              className="px-6 py-2 bg-blue-600 text-white rounded cursor-pointer border-none hover:bg-blue-700"
            >
              SAVE RULE
            </button>
          </div>
        </div>
      </div>

      <ConfirmationModal
        visible={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmSave}
        title="Confirm Rate Rule"
        message={renderConfirmationMessage()}
        confirmText="Yes, Save Rule"
        cancelText="Cancel"
        type="info"
      />
    </>
  );
};

export default RateAddEditModal;